import type { ReactNode } from "react";
import React from "react";

import { OfflineModeBlankView } from "@/components";
import { constantsData } from "@/data";

export class NetworkErrorBoundary extends React.Component<{
    children: ReactNode,
    hasOfflineNavigation: boolean,
}, {
    capturedError?: Error | null,
    hasNetworkError: boolean,
}> {
    constructor(props: {
        children: ReactNode,
        hasOfflineNavigation: boolean,
    }) {
        super(props);

        this.state = {
            capturedError: null,
            hasNetworkError: false,
        };
    }

    static getDerivedStateFromError(error: Partial<Error> & { code?: string }) {
        return { capturedError: (error as Error) ?? null };
    }

    componentDidCatch(error: Partial<Error> & { code?: string }) {
        const errorName = error?.name ?? "";

        const errorMessage = error?.message ?? "";

        const errorCode = error?.code ?? "";

        const checkByName = constantsData.others.networkErrors.names.includes(errorName);

        const checkByCode = constantsData.others.networkErrors.codes.includes(errorCode);

        const regexes = constantsData.others.networkErrors.messagePatterns.map((p: string | RegExp) => (p instanceof RegExp ? p : new RegExp(
            String(p),
            "i",
        )));

        const checkByMessage = regexes.some((rx: RegExp) => rx.test(errorMessage));

        const isNetworkError = errorName === constantsData.others.networkErrors.typeError ? checkByCode || checkByMessage : checkByName || checkByCode || checkByMessage;

        if (isNetworkError) this.setState({ hasNetworkError: true });

        else this.setState({ hasNetworkError: false });
    }

    render() {
        if (this.props.hasOfflineNavigation) return <OfflineModeBlankView />; // eslint-disable-line

        if (this.state.capturedError) { // eslint-disable-line
            if (this.state.hasNetworkError) return <OfflineModeBlankView />; // eslint-disable-line

            // Throw non network error to be caught by parent error boundary
            throw this.state.capturedError; // eslint-disable-line
        }

        return this.props.children; // eslint-disable-line
    }
}
